import { createApi } from '@reduxjs/toolkit/query/react'
import { type BasePageResponse, type BaseResponse } from '../models'
import {
  type AccountOpeningCreateRequest,
  type AccountOpeningDTO,
  type AccountOpeningStatus,
  type AccountOpeningUpdateRequest,
} from '../../models/AccountOpening'
import { type PageRequest } from '../../models/PageRequest'
import { removeEmptyItems } from '../../common/util'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

const _BASE_URL = import.meta.env.REACT_APP_INTEGRATIONS_SERVICE_API_BASE_URL!

type CachedQueryState = Parameters<typeof accountOpeningApi.util.selectCachedArgsForQuery>[0]

export const replaceOpeningInPage = (page: BasePageResponse<AccountOpeningDTO[]>, opening: AccountOpeningDTO): void => {
  const index = page.data?.findIndex((cached) => cached.id === opening.id) ?? -1
  if (index >= 0) {
    page.data[index] = opening
  }
}

interface ListAccountOpeningsRequest {
  accountId: string
  status?: AccountOpeningStatus
  connector?: string
  contactId?: string
  pageRequest?: PageRequest
}

export const accountOpeningApi = createApi({
  reducerPath: 'accountOpeningApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['AccountOpening'],
  endpoints: (builder) => ({
    createAccountOpening: builder.mutation<BaseResponse<{ id: string }>, { accountId: string; body: AccountOpeningCreateRequest }>({
      query: ({ accountId, body }) => ({
        url: `/accounts/${accountId}/account-openings/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'AccountOpening', id: 'LIST' }],
    }),
    listAccountOpenings: builder.query<BasePageResponse<AccountOpeningDTO[]>, ListAccountOpeningsRequest>({
      query: ({ accountId, status, connector, contactId, pageRequest }) => ({
        url: `/accounts/${accountId}/account-openings/`,
        params: removeEmptyItems({ status, connector, contactId, ...(pageRequest ? { page: pageRequest.page, size: pageRequest.size } : {}) }),
      }),
      providesTags: [{ type: 'AccountOpening', id: 'LIST' }],
    }),
    getAccountOpening: builder.query<BaseResponse<AccountOpeningDTO>, { accountId: string; openingId: string }>({
      query: ({ accountId, openingId }) => ({
        url: `/accounts/${accountId}/account-openings/${openingId}`,
      }),
      providesTags: (_result, _error, { openingId }) => [{ type: 'AccountOpening', id: openingId }],
    }),
    updateAccountOpening: builder.mutation<BaseResponse<AccountOpeningDTO>, { accountId: string; openingId: string; body: AccountOpeningUpdateRequest }>({
      query: ({ accountId, openingId, body }) => ({
        url: `/accounts/${accountId}/account-openings/${openingId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { openingId }) => [
        { type: 'AccountOpening', id: openingId },
        { type: 'AccountOpening', id: 'LIST' },
      ],
    }),

    refreshAccountOpeningStatus: builder.mutation<BaseResponse<AccountOpeningDTO>, { accountId: string; openingId: string }>({
      query: ({ accountId, openingId }) => ({
        url: `/accounts/${accountId}/account-openings/${openingId}/refresh-status`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { openingId }) => [{ type: 'AccountOpening', id: openingId }],
      async onQueryStarted({ accountId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled
          const cachedArgs = accountOpeningApi.util.selectCachedArgsForQuery(getState() as CachedQueryState, 'listAccountOpenings')
          cachedArgs
            .filter((args) => args.accountId === accountId)
            .forEach((args) => {
              dispatch(
                accountOpeningApi.util.updateQueryData('listAccountOpenings', args, (draft) => {
                  replaceOpeningInPage(draft, response.data)
                }),
              )
            })
        } catch {
          // the mutation's own error state drives the failure UI; no cache patch needed
        }
      },
    }),
    deleteAccountOpening: builder.mutation<void, { accountId: string; openingId: string }>({
      query: ({ accountId, openingId }) => ({
        url: `/accounts/${accountId}/account-openings/${openingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'AccountOpening', id: 'LIST' }],
    }),
  }),
})

export const {
  useCreateAccountOpeningMutation,
  useListAccountOpeningsQuery,
  useLazyGetAccountOpeningQuery,
  useUpdateAccountOpeningMutation,
  useRefreshAccountOpeningStatusMutation,
  useDeleteAccountOpeningMutation,
} = accountOpeningApi
